<template>
  <!-- v-if : à la déconnexion la session est vidée avant la redirection ; sans utilisateur, rien à afficher. -->
  <div v-if="$session.user" class="flex min-h-screen">
    <!-- Voile mobile -->
    <Transition name="fade">
      <div v-if="menuOpen" class="fixed inset-0 z-40 bg-slate-900/40 lg:hidden" @click="menuOpen = false" />
    </Transition>

    <!-- Sidebar -->
    <aside
      class="fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform duration-200 dark:border-slate-700 dark:bg-slate-800 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0"
      :class="menuOpen ? 'translate-x-0' : '-translate-x-full'"
      aria-label="Navigation principale"
    >
      <div class="border-b border-slate-100 p-6 dark:border-slate-700">
        <div class="flex items-center gap-3">
          <div class="flex h-11 w-11 shrink-0 items-center justify-center">
            <img src="@/assets/logo.png" alt="Logo GesRecette" class="h-11 w-11 rounded-xl object-contain" />
          </div>
          <div class="min-w-0">
            <div class="brand truncate font-bold text-slate-900 dark:text-slate-100">GesRecette</div>
            <div class="truncate text-xs text-slate-500 dark:text-slate-400">{{ $session.organization ? $session.organization.name : '' }}</div>
          </div>
        </div>
      </div>

      <nav class="flex-1 overflow-y-auto p-3">
        <template v-for="section in sections" :key="section.title">
          <div class="px-3 pb-2 pt-4 text-xs uppercase tracking-wider text-slate-400 first:pt-2">{{ section.title }}</div>
          <router-link
            v-for="item in section.items"
            :key="item.to"
            :to="item.to"
            class="sidebar-item"
            :class="{ active: isActive(item) }"
            @click="menuOpen = false"
          >
            <AppIcon :name="item.icon" />
            <span>{{ item.label }}</span>
          </router-link>
        </template>
      </nav>

      <div class="relative border-t border-slate-100 p-3 dark:border-slate-700">
        <router-link to="/profile" class="flex items-center gap-3 rounded-lg p-2 hover:bg-slate-50 dark:hover:bg-slate-700" @click="menuOpen = false">
          <div class="gradient-brand flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white">{{ initials }}</div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{{ $session.user.name }}</div>
            <div class="truncate text-xs text-slate-500 dark:text-slate-400">{{ roleLabel }}</div>
          </div>
        </router-link>
        <button type="button" class="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-rose-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-rose-400" @click="logout">
          <AppIcon name="logout" />
          Se déconnecter
        </button>
      </div>
    </aside>

    <!-- Contenu -->
    <div class="flex min-w-0 flex-1 flex-col">
      <header class="sticky top-0 z-30 border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80 sm:px-8">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <button type="button" class="btn-icon h-10 w-10 lg:hidden" aria-label="Ouvrir le menu" @click="menuOpen = true">
              <AppIcon name="menu" />
            </button>
            <div class="hidden text-sm text-slate-500 sm:block dark:text-slate-400">
              <span class="font-medium text-slate-700 dark:text-slate-200">{{ today }}</span>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <span v-if="rt.status === 'connected'" class="hidden items-center gap-1.5 text-xs font-semibold text-emerald-600 sm:inline-flex dark:text-emerald-400" title="Les données se mettent à jour en direct">
              <span class="h-2 w-2 rounded-full bg-emerald-500" />En direct
            </span>
            <span v-if="$session.organization" class="hidden rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 md:inline-block dark:bg-slate-700 dark:text-slate-300" title="Devise et fuseau de l'organisation">
              {{ $session.currency }} · {{ $session.timezone }}
            </span>
            <ThemeToggle class="h-10 w-10" />
            <NotificationBell />
          </div>
        </div>
      </header>

      <main class="flex-1 p-4 sm:p-8">
        <router-view v-slot="{ Component, route }">
          <component :is="Component" :key="route.path" />
        </router-view>
      </main>
    </div>
  </div>
</template>

<script>
import AppIcon from '@/components/ui/AppIcon.vue'
import ThemeToggle from '@/components/ui/ThemeToggle.vue'
import NotificationBell from './NotificationBell.vue'
import { NAV_SECTIONS } from '@/config/navigation'
import { session } from '@/stores/session'
import { enumLabel } from '@/utils/labels'
import { formatLongDate } from '@/utils/dates'
import { realtime } from '@/stores/realtime'

export default {
  name: 'AppLayout',
  components: { AppIcon, ThemeToggle, NotificationBell },
  setup() {
    return { rt: realtime }
  },
  data() {
    return { menuOpen: false }
  },
  computed: {
    sections() {
      return NAV_SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => session.can(i.permission)) })).filter((s) => s.items.length)
    },
    initials() {
      return ((session.user && session.user.name) || '?')
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    },
    // Affichage seulement : aucune logique ne repose sur le nom du rôle.
    roleLabel() {
      return enumLabel('role', session.user ? session.user.role : '')
    },
    today() {
      const label = formatLongDate(session.today())
      return label.charAt(0).toUpperCase() + label.slice(1)
    }
  },
  watch: {
    '$route.path'() {
      this.menuOpen = false
    }
  },
  methods: {
    isActive(item) {
      const base = item.match || item.to
      return this.$route.path === item.to || this.$route.path.startsWith(`${base}/`) || (item.match && this.$route.path === item.match)
    },
    async logout() {
      await session.logout()
      this.$router.replace({ name: 'login' })
    }
  }
}
</script>
